/**
 * Layout helpers for GenericGrid.
 *
 * Items are capped at 480px so a row never stretches its cards. Because of that
 * cap the items are centered rather than left aligned — including a trailing
 * short row, which is why this is a wrapping flex row and not a CSS grid (grid
 * cannot center a partial last row).
 *
 * The column count per breakpoint is not simply the maximum that fits — it is
 * the number that spreads the items over as few rows as possible while keeping
 * those rows as even as possible:
 *
 *   rows = ceil(items / maxColumns)
 *   columns = ceil(items / rows)
 *
 * With a maximum of 4 that gives 5 -> 3+2, 6 -> 3+3, 7 -> 4+3, 9 -> 3+3+3,
 * 12 -> 4+4+4. It also never exceeds the item count, so 2 items occupy 2
 * columns instead of sitting in the leftmost of four.
 */

export const containerClassName = "flex flex-wrap justify-center gap-4";

/**
 * Flex bases per column count, derived from `(100% - (n-1) * gap) / n`.
 * Written out literally so Tailwind can pick them up.
 */
const BASIS = {
  base: ["", "basis-full", "basis-[calc((100%_-_1rem)/2)]", "basis-[calc((100%_-_2rem)/3)]", "basis-[calc((100%_-_3rem)/4)]"],
  sm: ["", "sm:basis-full", "sm:basis-[calc((100%_-_1rem)/2)]", "sm:basis-[calc((100%_-_2rem)/3)]", "sm:basis-[calc((100%_-_3rem)/4)]"],
  md: ["", "md:basis-full", "md:basis-[calc((100%_-_1rem)/2)]", "md:basis-[calc((100%_-_2rem)/3)]", "md:basis-[calc((100%_-_3rem)/4)]"],
  lg: ["", "lg:basis-full", "lg:basis-[calc((100%_-_1rem)/2)]", "lg:basis-[calc((100%_-_2rem)/3)]", "lg:basis-[calc((100%_-_3rem)/4)]"],
  xxl: ["", "2xl:basis-full", "2xl:basis-[calc((100%_-_1rem)/2)]", "2xl:basis-[calc((100%_-_2rem)/3)]", "2xl:basis-[calc((100%_-_3rem)/4)]"],
} as const;

/** Same, for an item spanning two columns: `2 * single + gap`. */
const MAX_WIDTH = "max-w-[480px]";

/** Fewest rows that fit, then the evenest column count for those rows. */
export function balancedColumns(itemCount: number, maxColumns: number) {
  if (itemCount <= 0) return 1;
  const rows = Math.ceil(itemCount / maxColumns);
  return Math.ceil(itemCount / rows);
}

/** Maximum columns per breakpoint, widest viewport last. */
const MAX_COLUMNS = {
  event: { base: 1, sm: 2, lg: 3, xxl: 4 },
  default: { base: 1, md: 2, lg: 2, xxl: 3 },
} as const;

/**
 * Classes for the wrapper around a single card. Every item gets the same
 * single-column width — the 480px cap applies to the first card too.
 */
export function itemClassName(isEventGrid: boolean, itemCount: number) {
  const breakpoints = isEventGrid ? MAX_COLUMNS.event : MAX_COLUMNS.default;

  const bases = Object.entries(breakpoints).map(([breakpoint, maxColumns]) => {
    const columns = balancedColumns(itemCount, maxColumns);
    return BASIS[breakpoint as keyof typeof BASIS][columns];
  });

  // items-start: the wrapper stretches to the row height, but the card keeps its
  // own (image-derived) height instead of being stretched to fill the row.
  return ["flex items-start min-w-0 grow-0", MAX_WIDTH, ...bases].join(" ");
}
